import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, RefreshCw, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const STATUS_COLORS = {
  delivered: 'bg-green-100 text-green-700',
  read: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-blue-100 text-blue-700',
  queued: 'bg-amber-100 text-amber-700',
  sending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  undelivered: 'bg-red-100 text-red-700',
  skipped: 'bg-slate-100 text-slate-600',
};

export default function AdminWhatsAppLogs() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const timerRef = useRef(null);
  const [filters, setFilters] = useState({
    direction: '', status: '', phone: '', ticket: '',
  });

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/stats?days=7`, { headers: authHeaders() });
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/admin/whatsapp/logs`, { headers: authHeaders(), params });
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(pg);
    } catch {
      toast.error('No se pudieron cargar los logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchStats(); fetchLogs(1); }, [fetchStats, fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) { if (timerRef.current) clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => { fetchStats(); fetchLogs(page); }, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, fetchStats, fetchLogs, page]);

  const StatCard = ({ icon: Icon, label, value, color = 'bg-slate-100 text-slate-700', testId }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`p-2.5 rounded-lg ${color}`}><Icon size={18} /></div>
      <div>
        <p className="text-xs text-slate-500 leading-tight">{label}</p>
        <p className="text-xl font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67] flex items-center gap-2">
              <MessageSquare size={20} /> Logs de WhatsApp
            </h1>
            <p className="text-sm text-slate-500">Mensajes entrantes y salientes (últimos 7 días en métricas).</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchLogs(1); }}
              data-testid="wa-refresh">
              <RefreshCw size={14} className="mr-2" /> Actualizar
            </Button>
            <Button variant={autoRefresh ? 'default' : 'outline'} size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'bg-[#009940] hover:bg-[#007F35] text-white' : ''}
              data-testid="wa-autorefresh-toggle">
              Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
            <StatCard icon={MessageSquare} label="Total (7d)" value={stats.total} testId="wa-stat-total" />
            <StatCard icon={ArrowDownToLine} label="Entrantes" value={stats.inbound} color="bg-blue-100 text-blue-700" testId="wa-stat-inbound" />
            <StatCard icon={ArrowUpFromLine} label="Salientes" value={stats.outbound} color="bg-purple-100 text-purple-700" testId="wa-stat-outbound" />
            <StatCard icon={CheckCircle2} label="Entregados" value={stats.delivered} color="bg-green-100 text-green-700" testId="wa-stat-delivered" />
            <StatCard icon={Clock} label="En cola" value={stats.queued} color="bg-amber-100 text-amber-700" testId="wa-stat-queued" />
            <StatCard icon={AlertTriangle} label={`Fallidos (${stats.delivery_rate}% ok)`} value={stats.failed} color="bg-red-100 text-red-700" testId="wa-stat-failed" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Dirección</Label>
              <Select value={filters.direction || 'all'} onValueChange={v => setFilters({...filters, direction: v === 'all' ? '' : v})}>
                <SelectTrigger data-testid="wa-filter-direction"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="inbound">Entrantes</SelectItem>
                  <SelectItem value="outbound">Salientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Estado Twilio</Label>
              <Select value={filters.status || 'all'} onValueChange={v => setFilters({...filters, status: v === 'all' ? '' : v})}>
                <SelectTrigger data-testid="wa-filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="delivered">delivered</SelectItem>
                  <SelectItem value="read">read</SelectItem>
                  <SelectItem value="sent">sent</SelectItem>
                  <SelectItem value="queued">queued</SelectItem>
                  <SelectItem value="failed">failed</SelectItem>
                  <SelectItem value="undelivered">undelivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Teléfono</Label>
              <Input placeholder="+34..." value={filters.phone} onChange={e => setFilters({...filters, phone: e.target.value})} data-testid="wa-filter-phone" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Ticket</Label>
              <Input placeholder="MAJ-..." value={filters.ticket} onChange={e => setFilters({...filters, ticket: e.target.value})} data-testid="wa-filter-ticket" />
            </div>
            <div className="flex items-end">
              <Button onClick={() => fetchLogs(1)} className="w-full" data-testid="wa-apply-filters">Aplicar</Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">{total} mensajes</h3>
          </div>
          {loading && logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Cargando…</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="wa-logs-empty">Sin mensajes para los filtros seleccionados</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Fecha</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Dir.</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Tipo</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Teléfono</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Ticket</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Estado</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, idx) => (
                    <tr key={l.id || idx} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{(l.created_at || '').replace('T', ' ').slice(0, 19)}</td>
                      <td className="px-4 py-2.5">
                        {l.direction === 'inbound' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-700"><ArrowDownToLine size={12} /> IN</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-purple-700"><ArrowUpFromLine size={12} /> OUT</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs">{l.kind}</td>
                      <td className="px-4 py-2.5 text-xs font-mono">{l.direction === 'inbound' ? l.from : l.to}</td>
                      <td className="px-4 py-2.5 text-xs font-mono">{l.ticket_code || '—'}</td>
                      <td className="px-4 py-2.5">
                        {l.twilio_status ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_COLORS[l.twilio_status] || 'bg-slate-100 text-slate-600'}`}>
                            {l.twilio_status}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-700 max-w-md truncate" title={l.body}>{l.body}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">Página {page} de {pages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => fetchLogs(page - 1)} disabled={page === 1}>Anterior</Button>
                <Button variant="outline" size="sm" onClick={() => fetchLogs(page + 1)} disabled={page === pages}>Siguiente</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
